// The learner's best score per practice set of the Sanskrit games, kept on this device like the rest of the Sanskrit
// progress (localStorage `vani:practice-sets`, keyed by the bank: game, level and topics).

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'vani:practice-sets'

export interface SanskritSetRecord {
    best: number
    size: number
    plays: number
    at: string
}

type AllRecords = Record<string, Record<string, SanskritSetRecord>>

const readAll = (): AllRecords => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        const parsed: unknown = raw ? JSON.parse(raw) : {}

        return parsed && typeof parsed === 'object' ? parsed as AllRecords : {}
    }
    catch {
        return {}
    }
}

/** The records of one bank, read after mount, and a way to save a finished set. */
export const useSanskritSets = (bankKey: string | null) => {
    const [records, setRecords] = useState<Record<string, SanskritSetRecord>>({})
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        if (!bankKey) return

        setRecords(readAll()[bankKey] ?? {})
        setLoaded(true)
    }, [bankKey])

    const save = useCallback((set: number, correct: number, size: number) => {
        if (!bankKey) return

        const all = readAll()
        const mine = all[bankKey] ?? {}
        const before = mine[String(set)]

        mine[String(set)] = {
            best: Math.max(before?.best ?? 0, correct),
            size,
            plays: (before?.plays ?? 0) + 1,
            at: new Date().toISOString()
        }
        all[bankKey] = mine

        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
        }
        catch {
            // Storage full or blocked: the score still shows for this visit.
        }

        setRecords({ ...mine })
    }, [bankKey])

    return { records, loaded, save }
}
